import { EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { MenuProps } from 'antd/lib'
import { useState } from 'react'

import {
  useClassifierServiceDeleteRiskGroupClassifierElementEndpoint,
  useClassifierServiceGetRiskGroupClassifierElementListEndpoint,
  useClassifierServiceGetRiskGroupClassifierElementListEndpointKey
} from 'src/api/queries'
import {
  ApiError,
  GetCuatmClassifierElementListResponseItem,
  Permissions
} from 'src/api/requests'
import { DeleteConfirm, message, TableActionButtons } from 'src/components'
import { PageBreadcrumbs } from 'src/components/PageBreadcrumbs'
import { useUser } from 'src/hooks'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { RiskGroupModal } from '../components'

export const RiskGroupsClassifierPage = () => {
  const { t, currentLang } = useLocales()

  const [isOpen, setIsOpen] = useState(false)
  const [currentId, setCurrentId] = useState<string>()

  const { hasPermission } = useUser()

  const queryClient = useQueryClient()

  const { data, isLoading } =
    useClassifierServiceGetRiskGroupClassifierElementListEndpoint()

  const { mutate } =
    useClassifierServiceDeleteRiskGroupClassifierElementEndpoint({
      onError: (error: ApiError) => formErrorHandler(error.body.errors),
      onSuccess: () => {
        message.success(t('risk_groups.success_delete'))
        queryClient.invalidateQueries({
          queryKey: [
            useClassifierServiceGetRiskGroupClassifierElementListEndpointKey
          ]
        })
      }
    })

  const onToggle = () => setIsOpen((prev) => !prev)

  const onCloseModal = () => {
    onToggle()
    setCurrentId(undefined)
  }

  const getActionItems = (id: string): MenuProps['items'] => [
    {
      key: 'edit',
      icon: <EditOutlined />,
      label: t('common.edit'),
      onClick: () => {
        setCurrentId(id)
        onToggle()
      }
    },
    {
      key: 'divider',
      type: 'divider'
    },
    ...(hasPermission(Permissions.CLASSIFIER_RISK_GROUP_DELETE_ALL)
      ? [
          {
            key: 'delete',
            label: (
              <DeleteConfirm
                title={t('risk_groups.delete_title')}
                onConfirm={() => mutate({ elementId: id })}
              />
            ),
            danger: true
          }
        ]
      : [])
  ]

  const columns: ColumnsType<GetCuatmClassifierElementListResponseItem> = [
    {
      key: 'title',
      render: (_, { translation }) => translation?.[currentLang?.value]
    },
    {
      key: 'actions',
      width: 80,
      render: ({ id }) => (
        <TableActionButtons justify="center" items={getActionItems(id)} />
      )
    }
  ]

  return (
    <PageBreadcrumbs
      title={t('menu.risk_groups')}
      items={[
        {
          title: t('menu.risk_groups')
        }
      ]}
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={onToggle}>
          {t('risk_groups.add_new_group')}
        </Button>
      }
      extraProps={{ align: 'flex-end' }}
    >
      <Table
        loading={isLoading}
        dataSource={data?.data}
        pagination={false}
        bordered
        columns={columns}
        rowKey="id"
        expandable={{
          expandIcon: undefined
        }}
      />

      {isOpen && <RiskGroupModal onCancel={onCloseModal} id={currentId} />}
    </PageBreadcrumbs>
  )
}
